/**
 * Scheduler Service
 * Handles contract assessment meeting scheduling and management
 */
import BaseApiService from '../base/BaseApiService';
import type {
  Contract,
  MeetingDateSuggestion,
  ScheduleItem,
  ScheduleItemsResponse,
  GroupContractsResponse
} from '@app-types';

class SchedulerService extends BaseApiService {
  /**
   * Schedule a meeting for a contract
   */
  async scheduleMeeting(contractId: number, datetime: string): Promise<Contract> {
    return this.post<Contract>(`/contracts/${contractId}/schedule-meeting`, { datetime });
  }

  /**
   * Delete a contract meeting
   */
  async deleteMeeting(contractId: number): Promise<void> {
    await this.delete(`/contracts/${contractId}/meeting`);
  }

  /**
   * Get meeting date suggestions with AI-powered recommendations
   */
  async getMeetingDateSuggestions(itemId: number, type: 'contract' | 'group'): Promise<MeetingDateSuggestion[]> {
    const params = new URLSearchParams();
    params.append('id', itemId.toString());
    params.append('type', type);

    const response = await this.get<{ availableSlots: MeetingDateSuggestion[] }>(
      `/contract-assessment-meetings/time-slots?${params}`
    );

    // Sort by confidence (high > medium > low) and score (descending), then take top 6
    const sortedSlots = [...response.availableSlots]
      .sort((a, b) => {
        // First sort by confidence level
        const confidenceOrder = { high: 3, medium: 2, low: 1 };
        const confidenceDiff = confidenceOrder[b.confidence] - confidenceOrder[a.confidence];

        if (confidenceDiff !== 0) {
          return confidenceDiff;
        }

        // Then sort by score (descending)
        return b.score - a.score;
      })
      .slice(0, 6); // Take only top 6 suggestions

    return sortedSlots;
  }

  /**
   * Get meeting status for a contract
   */
  async getMeetingStatus(contractId: number): Promise<Contract> {
    return this.get<Contract>(`/contracts/${contractId}`);
  }

  /**
   * Get schedule items with filters
   */
  async getScheduleItems(
    filters: {
      searchTerm?: string;
      searchColumns?: string[];
      columnFilters?: string;
      state?: string;
      processedByMe?: boolean;
      userId?: number;
      sortBy?: string;
      order?: 'ASC' | 'DESC';
      limit?: number;
      offset?: number;
      viewMode?: 'assessor' | 'scheduler';
    } = {}
  ): Promise<ScheduleItemsResponse> {
    const params = new URLSearchParams();

    if (filters.searchTerm && filters.searchColumns) {
      params.append('searchTerm', filters.searchTerm);
      params.append('searchColumns', filters.searchColumns.join(','));
    }
    if (filters.columnFilters) params.append('columnFilters', filters.columnFilters);
    if (filters.state) params.append('state', filters.state);
    if (filters.processedByMe) params.append('processedByMe', 'true');
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
    if (filters.viewMode) params.append('viewMode', filters.viewMode);

    return this.get<ScheduleItemsResponse>(`/contract-assessment-meetings?${params}`);
  }

  /**
   * Get all schedule items matching the given filters, ignoring pagination — used
   * for full-dataset export. Shared by both the Scheduler and Assessor Meetings
   * pages (differentiated by `viewMode`/`state`), same as `getScheduleItems`.
   */
  async getScheduleItemsForExport(
    filters: {
      searchTerm?: string;
      searchColumns?: string[];
      columnFilters?: string;
      state?: string;
      processedByMe?: boolean;
      sortBy?: string;
      order?: 'ASC' | 'DESC';
      viewMode?: 'assessor' | 'scheduler';
    } = {}
  ): Promise<{ total: number; count: number; data: ScheduleItem[] }> {
    const params = new URLSearchParams();

    if (filters.searchTerm && filters.searchColumns) {
      params.append('searchTerm', filters.searchTerm);
      params.append('searchColumns', filters.searchColumns.join(','));
    }
    if (filters.columnFilters) params.append('columnFilters', filters.columnFilters);
    if (filters.state) params.append('state', filters.state);
    if (filters.processedByMe) params.append('processedByMe', 'true');
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);
    if (filters.viewMode) params.append('viewMode', filters.viewMode);

    return this.get<{ total: number; count: number; data: ScheduleItem[] }>(
      `/contract-assessment-meetings/export?${params}`
    );
  }

  /**
   * Group contracts together for scheduling
   */
  async groupContracts(data: { contract_ids: number[]; assessor_id: number }): Promise<GroupContractsResponse> {
    return this.post<GroupContractsResponse>('/contract-assessment-meetings/group-contracts', data);
  }

  /**
   * Add a single contract to an existing group
   */
  async addContractToGroup(data: { group_id: number; contract_id: number }): Promise<void> {
    await this.post(`/contract-assessment-meetings/add-contracts-to-group`, {
      contract_ids: [data.contract_id],
      group_id: data.group_id
    });
  }

  /**
   * Add multiple contracts to a group (used by both Scheduler and Assessor)
   */
  async addContractsToGroup(groupId: number, contractIds: number[]): Promise<void> {
    await this.post('/contract-assessment-meetings/add-contracts-to-group', {
      group_id: groupId,
      contract_ids: contractIds
    });
  }

  /**
   * Get compatible contracts that can be grouped together
   */
  async getCompatibleContracts(params: {
    state: string;
    assessorId?: number | null;
    managerIds?: number[];
    groupId?: number | null;
  }): Promise<{ total: number; data: ScheduleItem[] }> {
    const queryParams = new URLSearchParams();

    queryParams.append('state', params.state);

    // Send assessorId even if null to filter correctly
    if (params.assessorId !== undefined) {
      queryParams.append('assessorId', params.assessorId?.toString() || 'null');
    }

    // Send managerIds as JSON array (can be empty array for no managers)
    if (params.managerIds !== undefined) {
      queryParams.append('managerIds', JSON.stringify(params.managerIds));
    }

    // Send groupId to exclude contracts already in this group
    if (params.groupId !== undefined && params.groupId !== null) {
      queryParams.append('groupId', params.groupId.toString());
    }

    return this.get<{ total: number; data: ScheduleItem[] }>(
      `/contract-assessment-meetings/compatible-contracts?${queryParams.toString()}`
    );
  }

  /**
   * Remove contract from group (used by both Scheduler and Assessor)
   */
  async removeContractFromGroup(groupId: number, contractId: number): Promise<void> {
    const params = new URLSearchParams();
    params.append('contract_id', contractId.toString());
    params.append('group_id', groupId.toString());

    await this.delete(`/contract-assessment-meetings/remove-contract-from-group?${params}`);
  }

  /**
   * Update assessor for schedule item (used by both Scheduler and Assessor)
   */
  async updateScheduleItemAssessor(
    itemId: number | null,
    assessorId: number | null,
    contractId?: number,
    reason?: string
  ): Promise<void> {
    const params = new URLSearchParams();
    params.append('id', itemId?.toString() || contractId?.toString() || '0');
    params.append('type', itemId !== null ? 'group' : 'contract');

    const body: { assessor_id: number | null; notes?: string } = { assessor_id: assessorId };
    if (reason) {
      body.notes = reason;
    }

    await this.put(`/contract-assessment-meetings?${params}`, body);
  }

  /**
   * Update contract owners (used by both Scheduler and Assessor)
   */
  async updateContractOwners(itemId: number | null, contractId: number, ownerIds: number[]): Promise<void> {
    // Owners can be assigned to group OR contract (same as managers)
    const params = new URLSearchParams();
    params.append('id', itemId?.toString() || contractId?.toString() || '0');
    params.append('type', itemId !== null ? 'group' : 'contract');

    await this.put(`/contract-assessment-meetings?${params}`, {
      contract_owners: ownerIds
    });
  }

  /**
   * Update contract managers (used by both Scheduler and Assessor)
   */
  async updateContractManagers(itemId: number | null, contractId: number, managerIds: number[]): Promise<void> {
    // Managers can be assigned to group OR contract
    const params = new URLSearchParams();
    params.append('id', itemId?.toString() || contractId?.toString() || '0');
    params.append('type', itemId !== null ? 'group' : 'contract');

    await this.put(`/contract-assessment-meetings?${params}`, {
      contract_managers: managerIds
    });
  }

  /**
   * Schedule a meeting for a schedule item (contract or group)
   */
  async scheduleScheduleItemMeeting(
    itemId: number | null,
    contractId: number,
    datetime: string,
    reason?: string,
    notes?: string
  ): Promise<void> {
    const params = new URLSearchParams();

    // If itemId is null, it's a single contract (not grouped), otherwise it's a group
    if (itemId !== null) {
      params.append('id', itemId.toString());
      params.append('type', 'group');
    } else {
      params.append('id', contractId.toString());
      params.append('type', 'contract');
    }

    const body: { datetime: string; reason?: string; notes?: string } = { datetime };
    if (reason) {
      body.reason = reason;
    }
    if (notes) {
      body.notes = notes;
    }

    await this.post(`/contract-assessment-meetings/schedule?${params}`, body);
  }

  /**
   * Delete a scheduled meeting for a schedule item
   */
  async deleteScheduleItemMeeting(
    itemId: number | null,
    contractId: number,
    reason?: string,
    notes?: string
  ): Promise<void> {
    const params = new URLSearchParams();

    if (itemId) {
      // This is a group meeting
      params.append('id', itemId.toString());
      params.append('type', 'group');
    } else {
      // This is a single contract meeting
      params.append('id', contractId.toString());
      params.append('type', 'contract');
    }

    // Delete with body for reason and notes (if provided)
    const body: { reason?: string; notes?: string } = {};
    if (reason) {
      body.reason = reason;
    }
    if (notes) {
      body.notes = notes;
    }

    await this.delete(`/contract-assessment-meetings?${params}`, Object.keys(body).length > 0 ? body : undefined);
  }

  /**
   * Get single contract assessment meeting by ID
   */
  async getContractAssessmentMeeting(id: number): Promise<ScheduleItem> {
    return this.get<ScheduleItem>(`/contract-assessment-meetings/${id}`);
  }
}

export const schedulerService = new SchedulerService();
export default schedulerService;
